import {Color} from "../ui-elements/color";
import {CssVarHelper} from "../html-utils/css-var.helper";
import {StringUtils} from "../string-utils/string-utils";
import {AnalyticsService} from "../orchestration/analytics.service";
import {ResponsiveService} from "../orchestration/responsive.service";
import {SessionModel} from "../orchestration/session.model";
import {TranslateService} from "ng2-translate";
import {Injectable} from "@angular/core";
import {SessionEvents} from "../orchestration/session-events";
import {
    AppCardThemeData, AppCustomTextData,
    AppData,
    AppThemePaletteEntryData,
    AppThemeSettingValueData,
    ColorPairData
} from "../orchestration/app-entity.model";

// noinspection GrazieInspection, AiaStyle
@Injectable()
export class ThemeModel {
    private appliedAppCssVars: string[] = [];
    private appliedPaletteMap: { [paletteEntryName: string]: ColorPairData } = {};

    constructor(private sessionModel: SessionModel,
                private analyticsService: AnalyticsService,
                private responsiveService: ResponsiveService,
                private translate: TranslateService) {
        console.log("--- ThemeModel");

        SessionEvents.currentApp$.subscribe((appData: AppData) => {
            this.applyAppSettings(appData);
        });
    }

    private static readonly overridePaletteDefaults: { from: string, to: string } [] = [
        {from: "standard-page", to: "menu-page-image"},
        {from: "standard-page", to: "standard-page-image"},
        {from: "standard-page", to: "alert"},
        {from: "standard-page-header", to: "panel-header-footer"},
        {from: "panel-header-footer", to: "header-image"}
    ];

    public getPaletteEntry(name: string): ColorPairData {
        return this.appliedPaletteMap[name];
    }

    private applyAppSettings(appData: AppData): void {
        const cssVars: any = {};

        document.title = this.sessionModel.currentApp.appName;

        this.appliedPaletteMap = {};
        this.appliedAppCssVars.forEach((cssVar: string) => cssVars[cssVar] = undefined);

        // Apply specific app settings
        if (!appData.desktopPillarboxing) {
            cssVars["--woa-desktop-pillarbox-width"] = "10000px";
        }

        // Apply palette color-pairs and foreground-colors
        appData.palette.forEach((paletteEntry: AppThemePaletteEntryData) => {
            this.applyPaletteEntry(paletteEntry.name, paletteEntry.colorPair, cssVars);

            // Set derived dark/light colors
            if (paletteEntry.name === "panel") {
                [cssVars["--woa-theme-color-auto-dark-panel"], cssVars["--woa-theme-color-auto-light-panel"]] = Color.darkLightRGB(paletteEntry);
            }
        });

        // Handle defaults for known cases of context-overrides where the context color-pair is defaulted in scss,
        // and the override has not been set explicitly.
        ThemeModel.overridePaletteDefaults.forEach(({from, to}) => this.applyDefaultOverrides(from, to, cssVars));

        // Apply card themes
        if (this.sessionModel.currentApp.cardThemes) {
            this.sessionModel.currentApp.cardThemes.forEach((cardTheme: AppCardThemeData) => {
                const cardThemeName: string = cardTheme.name.replace(" ", "_");
                if (cardTheme.widthPx != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-width"] = cardTheme.widthPx + "px";
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-width"] = cardTheme.widthPx + "px";
                }
                if (cardTheme.heightPx != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-height"] = cardTheme.heightPx + "px";
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-height"] = cardTheme.heightPx + "px";
                }
                if (cardTheme.bodyPadding != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-body-padding"] = cardTheme.bodyPadding;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-body-padding"] = cardTheme.bodyPadding;
                }
                if (cardTheme.backgroundColor != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-background-color"] = cardTheme.backgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-background-color"] = cardTheme.backgroundColor;
                }
                if (cardTheme.maxColumnSpec != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-max-column-spec"] = cardTheme.maxColumnSpec;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-max-column-spec"] = cardTheme.maxColumnSpec;
                }
                if (cardTheme.headerBackgroundColor != undefined && cardTheme.headerBackgroundOpacity != undefined && cardTheme.headerTextColor != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-header-background-color"] = cardTheme.headerBackgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-header-background-opacity"] = cardTheme.headerBackgroundOpacity;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-header-text-color"] = cardTheme.headerTextColor;

                    cssVars["--woa-" + cardThemeName + "-themed-ticket-header-background-color"] = cardTheme.headerBackgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-header-background-opacity"] = cardTheme.headerBackgroundOpacity;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-header-text-color"] = cardTheme.headerTextColor;
                }
                if (cardTheme.footerBackgroundColor != undefined && cardTheme.footerBackgroundOpacity != undefined && cardTheme.footerTextColor != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-footer-background-color"] = cardTheme.footerBackgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-footer-background-opacity"] = cardTheme.footerBackgroundOpacity;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-footer-text-color"] = cardTheme.footerTextColor;

                    cssVars["--woa-" + cardThemeName + "-themed-ticket-footer-background-color"] = cardTheme.footerBackgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-footer-background-opacity"] = cardTheme.footerBackgroundOpacity;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-footer-text-color"] = cardTheme.footerTextColor;
                }
                if (cardTheme.placeholderBackgroundColor != undefined && cardTheme.placeholderOpacity != undefined && cardTheme.placeholderBorderColor != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-placeholder-background-color"] = cardTheme.placeholderBackgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-placeholder-opacity"] = cardTheme.placeholderOpacity;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-placeholder-border-color"] = cardTheme.placeholderBorderColor;

                    cssVars["--woa-" + cardThemeName + "-themed-ticket-placeholder-background-color"] = cardTheme.placeholderBackgroundColor;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-placeholder-opacity"] = cardTheme.placeholderOpacity;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-placeholder-border-color"] = cardTheme.placeholderBorderColor;
                }
                if (cardTheme.stampColor != undefined && cardTheme.stampOpacity != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-color"] = cardTheme.stampColor;
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-opacity"] = cardTheme.stampOpacity;

                    cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-color"] = cardTheme.stampColor;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-opacity"] = cardTheme.stampOpacity;
                }
                if (cardTheme.svgStampIcon != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-icon"] = cardTheme.svgStampIcon;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-icon"] = cardTheme.svgStampIcon;
                }
                if (cardTheme.stampIconScale != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-icon-scale"] = cardTheme.stampIconScale;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-icon-scale"] = cardTheme.stampIconScale;
                }
                if (cardTheme.stampIconRotateFactor != undefined) {
                    cssVars["--woa-" + cardThemeName + "-themed-loyalty-card-stamp-icon-rotate-factor"] = cardTheme.stampIconRotateFactor;
                    cssVars["--woa-" + cardThemeName + "-themed-ticket-stamp-icon-rotate-factor"] = cardTheme.stampIconRotateFactor;
                }
            });
        }

        // Apply custom style settings
        this.sessionModel.currentApp.themeSettings.forEach((setting: AppThemeSettingValueData) => {
            cssVars[setting.key] = setting.value;
        });

        this.appliedAppCssVars = Object.keys(cssVars);
        CssVarHelper.applyVars(document.documentElement, cssVars);

        // load fonts
        window["WebFont"].load({
            google: {
                families: ["Inter: 100,200,300,400,500,600,700,800,900"]
            }
        });

        // Apply app settings and custom text to the translation service
        this.translate.getTranslation("en").subscribe((translation) => {
            this.setTranslationKey(translation, "woa.common.appName", this.sessionModel.currentApp.appName);
            if (this.sessionModel.currentApp.shortAppName) {
                this.setTranslationKey(translation, "woa.common.shortAppName", this.sessionModel.currentApp.shortAppName);
            }

            if (this.sessionModel.currentApp.loginText1) {
                this.setTranslationKey(translation, "woa.message.entry.line1", this.sessionModel.currentApp.loginText1);
            }
            if (this.sessionModel.currentApp.loginText2) {
                this.setTranslationKey(translation, "woa.message.entry.line2", this.sessionModel.currentApp.loginText2);
            }

            if (this.sessionModel.currentApp.memberNumberName) {
                this.setTranslationKey(translation, "woa.common.memberNumberName", this.sessionModel.currentApp.memberNumberName);
            }

            if (this.sessionModel.currentApp.pointsName) {
                this.setTranslationKey(translation, "woa.common.pointsName", this.sessionModel.currentApp.pointsName.toLowerCase());
                this.setTranslationKey(translation, "woa.common.PointsName", StringUtils.titleCase(this.sessionModel.currentApp.pointsName));
            }

            if (this.sessionModel.currentApp.tableOrderingName) {
                this.setTranslationKey(translation, "woa.common.order.atTable.placement.noun.lc", this.sessionModel.currentApp.tableOrderingName.toLowerCase());
                this.setTranslationKey(translation, "woa.common.order.atTable.placement.noun.uc", StringUtils.titleCase(this.sessionModel.currentApp.tableOrderingName));

                if (this.sessionModel.currentApp.tableOrderingName.toLowerCase() === "room") {
                    this.setTranslationKey(translation, "woa.common.order.atTable.placement.orderPhrase.preposition", "in");
                    this.setTranslationKey(translation, "woa.common.order.atTable.number.noun.lc", "room number");
                    this.setTranslationKey(translation, "woa.common.order.atTable.number.noun.uc", "Room Number");
                }
            }

            this.sessionModel.currentApp.customTexts.forEach((appCustomText: AppCustomTextData) => {
                this.setTranslationKey(translation, appCustomText.key, appCustomText.value);
            });

            this.translate.setTranslation("en", translation, true);
        });

        this.responsiveService.recheckAvailableWidth();

        if (appData.metaPixelId) {
            this.analyticsService.initMetaPixel(appData.metaPixelId);
        }
        if (appData.googleTagId) {
            this.analyticsService.initGoogleTag(appData.googleTagId);
        }
    }

    private applyPaletteEntry(paletteEntryName: string, colorPair: ColorPairData, cssVars: any): void {
        if (colorPair.backgroundColor !== "n/a") {
            if (colorPair.backgroundColor) {
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-background"] = Color.colorStringToRGB(colorPair.backgroundColor);
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-background-shader"] = Color.colorStringToShaderRGB(colorPair.backgroundColor, true);
            }
            if (colorPair.foregroundColor) {
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-foreground"] = Color.colorStringToRGB(colorPair.foregroundColor);
                cssVars["--woa-theme-color-" + paletteEntryName + "-pair-foreground-shader"] = Color.colorStringToShaderRGB(colorPair.foregroundColor);
            }
        } else if (colorPair.foregroundColor) {
            const i: number = paletteEntryName.indexOf("/");
            if (i >= 0) {
                const pairName: string = paletteEntryName.substring(0, i);
                const variantName: string = paletteEntryName.substring(i + 1);
                cssVars["--woa-theme-color-" + pairName + "-pair-" + variantName + "-foreground"] = Color.colorStringToRGB(colorPair.foregroundColor);
            } else {
                cssVars["--woa-theme-color-" + paletteEntryName + "-foreground"] = Color.colorStringToRGB(colorPair.foregroundColor);
                cssVars["--woa-theme-color-" + paletteEntryName + "-foreground-shader"] = Color.colorStringToShaderRGB(colorPair.foregroundColor);
            }
        }

        // Handle additional defaults for overrides of palette entries which are both a color-pair and a foreground
        if (paletteEntryName.endsWith("--brand")) {
            cssVars["--woa-theme-color-" + paletteEntryName + "-foreground"] = Color.colorStringToRGB(colorPair.backgroundColor);
        }

        this.appliedPaletteMap[paletteEntryName] = colorPair;
    }

    /**
     * Where an override has been provided for fromContext, apply that same override for toContext if it has not been specified.
     * This caters for context palette entries which default from another context palette entry.
     * @param fromContext
     * @param toContext
     * @param cssVars records all applied CSS custom properties
     * @param assumeFromContextIsSet
     */
    private applyDefaultOverrides(fromContext: string, toContext: string, cssVars: any, assumeFromContextIsSet: boolean = false): void {
        if ((assumeFromContextIsSet || this.appliedPaletteMap[fromContext]) && !this.appliedPaletteMap[toContext]) {
            Object.keys(this.appliedPaletteMap).forEach((name: string) => {
                if (name.startsWith(fromContext + "--")) {
                    const overrideName: string = toContext + name.substring(fromContext.length);
                    if (!this.appliedPaletteMap[overrideName]) {
                        this.applyPaletteEntry(overrideName, this.appliedPaletteMap[name], cssVars);
                    }
                }
            });
        }
    }

    private setTranslationKey(translation: any, key: string, value: string): void {
        const keys = key.split(".");
        const root = keys.slice(0, keys.length - 1).reduce((entity, fieldName) => entity ? entity[fieldName] : undefined, translation);
        if (root) {
            root[keys[keys.length - 1]] = value;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/theme/theme.model.ts